import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, InputGroup, Spinner, Alert } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';
import vendorService from '../services/vendorService';
import userService from '../services/userService';

function EmployeesPage() {
  const auth = useAuth();
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        // Only allow sellers/admins
        const res = await vendorService.getForSelect({ per_page: 1000 });
        setVendors(res.vendors || res);
        // Preselect first vendor if any
        if ((res.vendors || res).length > 0) {
          setSelectedVendorId((res.vendors || res)[0].id);
        }
      } catch (err) {
        setError('Failed to load your companies');
      } finally {
        setLoading(false);
      }
    };

    if (!auth.loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.loading]);

  useEffect(() => {
    if (selectedVendorId) fetchEmployees(selectedVendorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVendorId]);

  const fetchEmployees = async (vendorId) => {
    try {
      setEmployees([]);
      const res = await vendorService.getEmployees(vendorId);
      setEmployees(res.employees || res);
    } catch (err) {
      setError('Failed to load employees for selected company');
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery) return;
    try {
      setSearching(true);
      setSearchResults([]);
      const res = await userService.searchUsers(searchQuery);
      // backends may return {users: [...] } or array
      const users = res.users || res;
      setSearchResults(users);
    } catch (err) {
      setError('User search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleAddEmployee = async (user) => {
    if (!selectedVendorId) return setError('Select a company first');
    try {
      await vendorService.addEmployee(selectedVendorId, { user_id: user.id });
      // refresh employees list
      await fetchEmployees(selectedVendorId);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to add employee');
    }
  };

  if (auth.loading || loading) {
    return (
      <Container fluid className="px-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
          <Spinner animation="border" />
        </div>
      </Container>
    );
  }

  if (!auth.isAuthenticated || !auth.isSeller) {
    return (
      <Container fluid className="px-4">
        <PageHeader title="Employees" subtitle="Manage company employees" />
        <Alert variant="warning">You do not have permission to view this page.</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="px-4">
      <PageHeader title="Employees" subtitle="Manage company employees" />

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <Row className="mb-3">
        <Col>
          <Card className="p-3">
            <Row>
              <Col md={4} className="mb-3 mb-md-0">
                <Form.Select value={selectedVendorId || ''} onChange={(e) => setSelectedVendorId(Number(e.target.value))}>
                  {(vendors || []).map(v => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={8}>
                <Form onSubmit={handleSearch}>
                  <InputGroup>
                    <Form.Control placeholder="Search users by email, username or id" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                    <Button type="submit" disabled={searching}>Search</Button>
                  </InputGroup>
                </Form>

                <div className="mt-3">
                  {searching && <div>Searching...</div>}
                  {searchResults.length > 0 && (
                    <Table hover size="sm">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Username</th>
                          <th>Email</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.map(u => (
                          <tr key={u.id}>
                            <td>{u.id}</td>
                            <td>{u.username}</td>
                            <td>{u.email}</td>
                            <td className="text-end">
                              <Button size="sm" onClick={() => handleAddEmployee(u)}>Add to Company</Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="p-3">
            <h5>Employees</h5>
            <Table hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Added At</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {employees.map(emp => (
                  <tr key={emp.id}>
                    <td>{emp.id}</td>
                    <td>{emp.username}</td>
                    <td>{emp.email}</td>
                    <td>{emp.role}</td>
                    <td>{emp.added_at}</td>
                    <td className="text-end">
                      <Button size="sm" variant="danger" onClick={async () => {
                        try {
                          await vendorService.removeEmployee(selectedVendorId, emp.id);
                          await fetchEmployees(selectedVendorId);
                        } catch (err) {
                          setError('Failed to remove employee');
                        }
                      }}>Remove</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default EmployeesPage;

